import { ReactElement, useCallback, useEffect } from 'react';
import { TextField } from '@mui/material';
import { useFormik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { useSubscriptionFeatures, useLazyRequest } from '~hooks/index';
import { createItemPreset } from '~services/item';
import { ItemPreset, ItemPresetParse } from '~services/item/types';
import { DialogConfimation } from '~components/index';
import { hasError } from '~utils/formHelpers';
import { convertItemSearchFiltersToItemOrCollectFilter } from '~utils/order';
import { camelCaseSerializer } from '~utils/serializer';
import { PlanFeaturesTypes } from '~globals/types/enums';

import { OrdersFiltersPresetsV2DialogCreateProps } from './types';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const OrdersFiltersPresetsV2DialogCreate = ({
  open,
  onClose,
  filterTabId,
  currentFilters,
  onSuccess,
}: OrdersFiltersPresetsV2DialogCreateProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const [, loadingCreatePreset, , executeCreatePreset] = useLazyRequest({
    request: createItemPreset,
    withPostSuccess: (response) => {
      const presetResponse = response.data?.data as ItemPreset;
      const parsePresetResponse: ItemPresetParse = {
        ...presetResponse,
        data: camelCaseSerializer.serialize(JSON.parse(presetResponse.data)),
      };
      const message = `El filtro ${parsePresetResponse.description} fue creado correctamente`;

      onSuccess(parsePresetResponse);

      enqueueSnackbar(message, { variant: 'success' });
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo crear el filtro, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const executeSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      const data = JSON.stringify(
        convertItemSearchFiltersToItemOrCollectFilter(
          filterTabId,
          currentFilters,
          {
            limitNoPro: hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
            mgmZones: hasAccessToFeature(PlanFeaturesTypes.MgmZones),
            limitPremium: hasAccessToFeature(PlanFeaturesTypes.LimitPremium),
          },
        ),
      );

      await executeCreatePreset({
        description: values[FIELDS_NAME.NAME],
        data,
      });

      setSubmitting(false);
    },
    [filterTabId, currentFilters, executeCreatePreset, hasAccessToFeature],
  );

  const {
    errors,
    touched,
    getFieldProps,
    submitForm,
    isSubmitting,
    dirty,
    isValid,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: executeSubmit,
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogConfimation
      open={open}
      title="Crear filtro personalizado"
      description="Defina un nombre para su filtro personalizado"
      confirmText="Guardar"
      onConfirm={submitForm}
      onCancel={onClose}
      disabledConfirm={!dirty || !isValid}
      loadingConfirm={isSubmitting || loadingCreatePreset}
    >
      <TextField
        label="Nombre"
        fullWidth
        error={hasError(touched, errors, FIELDS_NAME.NAME)}
        {...getFieldProps(FIELDS_NAME.NAME)}
      />
    </DialogConfimation>
  );
};

export default OrdersFiltersPresetsV2DialogCreate;
